/**
 * Storage service for module mwApp.common
 */

/**
 * storageSvc service
 * Work with session and local browser storage
 *
 * @class storageSvc
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc service
 * @name mwApp/common.service:storageSvc
 */
/* @ngInject */
export default function StorageSvc ($window, $log) {
  /**
   * default storage name
   *
   * @private
   * @memberof mwApp/common.service:storageSvc~
   * @type {String}
   */
  var defaultStorage = 'session'

  try {
    /**
     * Session storage
     *
     * @name mwApp/common.service:storageSvc#session
     * @member
     * @type {mwApp/common.service:storageSvc~WindowsStorageFacade}
     */
    this.session = new WindowsStorageFacade($window.sessionStorage) // limited to tab

    /**
     * Local storage
     *
     * @name mwApp/common.service:storageSvc#local
     * @member
     * @type {mwApp/common.service:storageSvc~WindowsStorageFacade}
     */
    this.local = new WindowsStorageFacade($window.localStorage, $window) // for domain, without expiration
  } catch (e) {
    $log.error('storageSvc : Unable access to storage\n', e)
    defaultStorage = 'memory'
  }

  /**
   * Memory storage
   *
   * @name mwApp/common.service:storageSvc#memory
   * @member
   * @type {mwApp/common.service:storageSvc~MemoryStorageFacade}
   */
  this.memory = new MemoryStorageFacade() // works only until refresh page

  /**
   * Cookies storage
   *
   * @name mwApp/common.service:storageSvc#cookies
   * @member
   * @type {mwApp/common.service:storageSvc~CookieStorageFacade}
   */
  this.cookies = new CookieStorageFacade() // save cookies for 1 year

  /**
   * Set default storage
   *
   * @name mwApp/common.service:storageSvc#setDefault
   * @function
   * @param {String} storageName - storage name to use as default
   */
  this.setDefault = function (storageName) {
    if (this[storageName] !== undefined) {
      angular.extend(this, this[storageName])
    }
  }

  this.setDefault(defaultStorage)

  /**
   * Set value to default storage
   *
   * @name mwApp/common.service:storageSvc#set
   * @function
   * @param {String} key
   * @param {*} value
   */

  /**
   * Get value from default storage
   *
   * @name mwApp/common.service:storageSvc#get
   * @function
   * @param  {String} key
   * @param  {*} defaultValue
   * @return {*}
   */

  /**
   * Remove item from default storage
   *
   * @name mwApp/common.service:storageSvc#remove
   * @function
   * @param  {String} key
   */

  /**
   * Clear default storage
   *
   * @name mwApp/common.service:storageSvc#clear
   * @function
   */
}

/**
 * Tries to return value same type, as default value
 *
 * @private
 * @memberof mwApp/common.service:storageSvc~
 * @param {*} o - value
 * @param {*} def - default value
 * @return {*}
 */
function likeDefault (o, def) {
  if (o === undefined || o === null) {
    return def
  }
  switch (typeof def) {
    case 'undefined':
      return o
    case 'number':
      return +o
    case 'string':
      return String(o)
    case 'boolean':
      return String(o) === 'true'
    case 'object':
      if (typeof o !== 'object') {
        try {
          return JSON.parse(o)
        } catch (e) {
          // Ignore
        }
      }
  }
  return o
}

/**
 * Facade for window.sessionStorage and window.localStorage
 *
 * @private
 * @class
 * @memberof mwApp/common.service:storageSvc~
 */
function WindowsStorageFacade (storage, window) {
  /**
   * Set value to storage
   *
   * @param {String} key
   * @param {*} value
   */
  this.set = function (key, value) {
    storage.setItem(key, typeof value === 'object' ? JSON.stringify(value) : String(value))
  }

  /**
   * Get value from storage
   *
   * @param  {String} key
   * @param  {*} defaultValue
   * @return {*}
   */
  this.get = function (key, defaultValue) {
    return likeDefault(storage.getItem(key), defaultValue)
  }

  /**
   * Remove item from storage
   *
   * @param  {String} key
   */
  this.remove = function (key) {
    storage.removeItem(key)
  }

  /**
   * Clear storage
   */
  this.clear = function () {
    storage.clear()
  }

  if (window) {
    /**
     * Add localStorage event listener
     */
    this.onChange = function (key, callback) {
      window.addEventListener('storage', function (e) {
        if (e.key === key) {
          callback(e)
        }
      })
    }
  }
}

/**
 * Facade for cookies
 *
 * @private
 * @class
 * @memberof mwApp/common.service:storageSvc~
 */
function CookieStorageFacade () {
  /**
   * Set cookie
   *
   * @private
   * @inner
   * @param {String} name  - cookie name
   * @param {String} value - cookie value
   * @param {Date|Number} [expires] - expiration, Date instance or number of days, can be negative
   * @param {String} [path]         - path
   * @param {String} [domain]       - domain
   */
  function setCookie (name, value, expires, path, domain) {
    var cookie = name + '=' + value + ';'

    if (expires) {
      if (expires instanceof Date) { // if it's a date
        if (isNaN(expires.getTime())) { // invalid date
          expires = new Date()
        }
      } else {
        expires = new Date(new Date().getTime() + parseInt(expires, 10) * 1000 * 60 * 60 * 24)
      }
      cookie += 'expires=' + expires.toGMTString() + ';'
    }
    if (path) {
      cookie += 'path=' + path + ';'
    }
    if (domain) {
      cookie += 'domain=' + domain + ';'
    }

    document.cookie = cookie
  }

  /**
   * Get cookie
   *
   * @private
   * @inner
   * @param {String} name - cookie name
   * @return {String} cookie value, if exists
   */
  function getCookie (name) {
    var cname = name + '='
    var ca = document.cookie.split(';')
    for (var i = 0; i < ca.length; i++) {
      var c = ca[i]
      while (c.charAt(0) === ' ') {
        c = c.substring(1)
      }
      if (c.indexOf(cname) === 0) {
        return c.substring(cname.length, c.length)
      }
    }
    return ''
  }

  /**
   * Delete cookie
   *
   * @private
   * @inner
   * @param {String} name - cookie name
   * @param {String} [path]   - path
   * @param {String} [domain] - domain
   */
  function deleteCookie (name, path, domain) {
    if (getCookie(name)) {
      setCookie(name, '', -1, path, domain)
    }
  }

  /**
   * Clear cookies
   *
   * @private
   * @inner
   */
  function clearCookies () {
    var cookies = document.cookie.split(';')
    for (var i = 0; i < cookies.length; i++) {
      var cookie = cookies[i]
      var eqPos = cookie.indexOf('=')
      var name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie
      document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT'
    }
  }

  /**
   * Set value to storage
   *
   * @param {String} key
   * @param {*} value
   */
  this.set = function (key, value) {
    setCookie(key, typeof value === 'object' ? JSON.stringify(value) : String(value), 365) // save for year
  }

  /**
   * Get value from storage
   *
   * @param  {String} key
   * @param  {*} defaultValue
   * @return {*}
   */
  this.get = function (key, defaultValue) {
    return likeDefault(getCookie(key), defaultValue)
  }

  /**
   * Remove item from storage
   *
   * @param  {String} key
   */
  this.remove = function (key) {
    deleteCookie(key)
  }

  /**
   * Clear storage
   */
  this.clear = function () {
    clearCookies()
  }
}

/**
 * Facade for saving strings and objects in memory <br>
 * Works only until refresh
 *
 * @private
 * @class
 * @memberof mwApp/common.service:storageSvc~
 */
function MemoryStorageFacade () {
  /**
   * inner store
   *
   * @type {Object}
   */
  var store = {}

  /**
   * Set value to storage
   *
   * @param {String} key
   * @param {*} value
   */
  this.set = function (key, value) {
    store[key] = value
  }

  /**
   * Get value from storage
   *
   * @param  {String} key
   * @param  {*} defaultValue
   * @return {*}
   */
  this.get = function (key, defaultValue) {
    return likeDefault(store[key], defaultValue)
  }

  /**
   * Remove item from storage
   *
   * @param  {String} key
   */
  this.remove = function (key) {
    delete store[key]
  }

  /**
   * Clear storage
   */
  this.clear = function () {
    store = {}
  }
}
